// SymbolRenderer.js � thin shim over mil-sym-ts-web.
// Provides the renderToDataURL / asSVG / ensureInit API consumed by SymbolGallery,
// SymbolEditor, TacticalPalette, SidcBuilder, and milsymbolSettings.
// The old MapLibre atlas pipeline (registerSymbol, clearCache) is gone;
// OL Icon style consumes SVG data URIs directly from MilStdIconRenderer.

import {
    MilStdIconRenderer,
    MilStdAttributes,
    RendererSettings,
    SymbolID,
    MSLookup,
    SectorModUtils,
    init,
    isReady,
} from '@armyc2.c5isr.renderer/mil-sym-ts-web';

// ponytail: singleton init promise so concurrent callers don't double-init.
let _initPromise = null;

export function ensureInit() {
    if (!_initPromise) {
        _initPromise = Promise.resolve()
            .then(() => { if (!isReady()) return init(); })
            .catch((e) => { console.warn('[SymbolRenderer] mil-sym-ts-web init error', e); });
    }
    return _initPromise;
}

// -- Affiliation palette (FM 1-02.2 Table 1-4) -----------------------------
export const DEFAULT_AFFILIATION_PALETTE = {
    'friend':          { fill: '#80e0ff', stroke: '#000000' },
    'assumed-friend':  { fill: '#80e0ff', stroke: '#000000' },
    'hostile':         { fill: '#ff8080', stroke: '#000000' },
    'suspect':         { fill: '#ff8080', stroke: '#000000' },
    'joker':           { fill: '#ff8080', stroke: '#000000' },
    'faker':           { fill: '#ff8080', stroke: '#000000' },
    'neutral':         { fill: '#aaffaa', stroke: '#000000' },
    'unknown':         { fill: '#ffff80', stroke: '#000000' },
    'pending':         { fill: '#ffff80', stroke: '#000000' },
    'none':            { fill: '#ffffff', stroke: '#000000' },
};

// -- Default symbol settings ------------------------------------------------
export const DEFAULT_SYMBOL_SETTINGS = {};

// -- renderToDataURL --------------------------------------------------------

export function buildModsMap(mods) {
    const m = new Map();
    if (!mods) return m;
    for (const [k, v] of Object.entries(mods)) {
        if (v != null && v !== '') m.set(k, String(v));
    }
    return m;
}

export function buildAttrsMap(_settings) {
    return new Map();
}

const _renderCache = new Map();

function _renderCacheKey(sidc, mods, settings) {
    return sidc + '|' + JSON.stringify(mods || {}) + '|' + JSON.stringify(settings || {});
}

// rendering-usetransition-loading / async-defer-await: lets a caller (SymbolEditor's preview)
// check synchronously, without paying any render cost, whether this exact sidc+mods+settings
// combo has already been rendered — so it can paint instantly on a cache hit and only defer to
// idle time on a genuine miss.
export function peekCachedDataURL(sidc, mods, settings) {
    const key = _renderCacheKey(sidc, mods, settings);
    return _renderCache.has(key) ? _renderCache.get(key) : undefined;
}

export function renderToDataURL(sidc, mods, settings) {
    const key = _renderCacheKey(sidc, mods, settings);
    if (_renderCache.has(key)) return _renderCache.get(key);
    try {
        const renderer = MilStdIconRenderer.getInstance();
        if (!renderer) return '';
        const info = renderer.RenderSVG(sidc, buildModsMap(mods), buildAttrsMap(settings || DEFAULT_SYMBOL_SETTINGS));
        if (!info) return '';
        const uri = info.getSVGDataURI();
        _renderCache.set(key, uri);
        return uri;
    } catch (_) { return ''; }
}

export function asSVG(sidc, mods, settings) {
    try {
        const renderer = MilStdIconRenderer.getInstance();
        if (!renderer) return '';
        const info = renderer.RenderSVG(sidc, buildModsMap(mods), buildAttrsMap(settings || DEFAULT_SYMBOL_SETTINGS));
        return info ? info.getSVG() : '';
    } catch (_) { return ''; }
}

// Legacy no-op kept for callers that imported clearCache
export function clearCache() { _renderCache.clear(); }

// ── Affiliation helper ──────────────────────────────────────────────────────
// Maps SymbolID standard-identity digit(s) → domain affiliation label.
// ponytail: SymbolID.getStandardIdentity(sidc) returns a numeric string like "3";
// we map it to the domain term from CONTEXT.md.
const _SI_TO_AFFILIATION = {
    '0': 'pending',
    '1': 'unknown',
    '2': 'assumed-friend',
    '3': 'friend',
    '4': 'neutral',
    '5': 'unknown',
    '6': 'hostile',
};

export function getAffiliationForSidc(sidc) {
    try {
        const si = SymbolID.getStandardIdentity(sidc);
        return _SI_TO_AFFILIATION[String(si)] ?? 'friend';
    } catch (_) {
        return 'friend';
    }
}

// ── Mobility helper (ticket 03) ─────────────────────────────────────────────
// Derives a coarse mobility classification from a SIDC's symbol set:
//   - Installation symbol sets (fixed emplacements/buildings) → 'stationary'
//   - Unit/equipment/platform symbol sets (things that move) → 'moving'
//   - Anything else (control measures, METOC, unknown/undeterminable) → null
//     (caller/UI treats null as "manual" — see mobility attr in mapSchema.js)
//
// Built from live `SymbolID.SymbolSet_*` constants rather than hardcoded numbers so this
// stays correct if the underlying mil-sym-ts-web catalogue ever renumbers them. Under the
// Jest mock (`src/__mocks__/@armyc2.c5isr.renderer/mil-sym-ts-web.js`) SymbolID is `{}`, so
// every constant is `undefined` and both sets end up empty — getMobilityForSidc degrades to
// always returning null there, same as getAffiliationForSidc degrades to 'friend' on error.
// Tests that need real behavior override the mock's SymbolID, same pattern as
// ElementPalette.test.jsx.
const _STATIONARY_SYMBOL_SETS = new Set([
    SymbolID.SymbolSet_LandInstallation,
    // ponytail: this catalogue version (MIL-STD-2525D) has no separate Sea/Air Installation
    // symbol sets — Land Installation is the only dedicated "fixed emplacement" set. If a future
    // catalogue version adds them, list the corresponding SymbolID constants here too.
].filter((v) => v != null));

const _MOVING_SYMBOL_SETS = new Set([
    SymbolID.SymbolSet_Air,
    SymbolID.SymbolSet_AirMissile,
    SymbolID.SymbolSet_Space,
    SymbolID.SymbolSet_SpaceMissile,
    SymbolID.SymbolSet_LandUnit,
    SymbolID.SymbolSet_LandCivilianUnit_Organization,
    SymbolID.SymbolSet_LandEquipment,
    SymbolID.SymbolSet_DismountedIndividuals,
    SymbolID.SymbolSet_SeaSurface,
    SymbolID.SymbolSet_SeaSubsurface,
    SymbolID.SymbolSet_MineWarfare,
].filter((v) => v != null));

/**
 * @param {string} sidc - MIL-STD-2525 SIDC string.
 * @returns {'stationary'|'moving'|null}
 */
export function getMobilityForSidc(sidc) {
    if (!sidc) return null;
    try {
        const ss = SymbolID.getSymbolSet(sidc);
        if (_STATIONARY_SYMBOL_SETS.has(ss)) return 'stationary';
        if (_MOVING_SYMBOL_SETS.has(ss)) return 'moving';
        return null;
    } catch (_) {
        return null;
    }
}

// ── MSLookup catalog helpers — shared by SidcBuilder and SymbolEditor ──────
// IDs from getIDList are 8-char BasicSymbolIDs: [symbolSet(2)][entity(2)][entityType(2)][entitySubType(2)]
// We use MSInfo.getName() which returns the leaf descriptor name directly.
//
// perf: getIDList(version) returns the ENTIRE MIL-STD-2525D catalog (~5,750 entries). The three
// builders below used to each do their own full linear scan + startsWith filter over that list —
// paid on every call, including every SymbolEditor mount (i.e. every time a placed symbol is
// (re)selected on the map, since SymbolEditor's useMemo caches don't survive its own unmount).
// Fixed with two module-level caches that persist across calls/mounts for the process lifetime
// (the catalog itself is static, so this is safe to cache forever):
//   1. `_idsBySymbolSet(version)` — indexes getIDList() once per version into symbolSet -> ids[],
//      so each builder only scans its own (much smaller) slice instead of the full catalog.
//   2. `_entityOptionsCache` / `_entityTypeOptionsCache` / `_entitySubTypeOptionsCache` — memoize
//      each builder's finished option array by its exact inputs, so repeat calls for the same
//      symbol (e.g. reselecting it, or any other component built on the same MSLookup data) are
//      O(1) lookups instead of re-deriving from the index.
const _idsBySymbolSetByVersion = new Map();

function _idsBySymbolSet(lookup, version) {
    let bySet = _idsBySymbolSetByVersion.get(version);
    if (bySet) return bySet;
    bySet = new Map();
    for (const id of lookup.getIDList(version)) {
        const ss = id.slice(0, 2);
        let list = bySet.get(ss);
        if (!list) { list = []; bySet.set(ss, list); }
        list.push(id);
    }
    _idsBySymbolSetByVersion.set(version, bySet);
    return bySet;
}

function _sortedOptions(seen) {
    return [...seen.entries()]
        .map(([code, label]) => ({ code, label }))
        .sort((a, b) => a.label.localeCompare(b.label));
}

const _entityOptionsCache = new Map();

export function buildEntityOptions(symbolSet) {
    if (_entityOptionsCache.has(symbolSet)) return _entityOptionsCache.get(symbolSet);
    let result;
    try {
        const lookup = MSLookup.getInstance();
        if (!lookup?.isReady()) return []; // not ready — don't cache
        const version = SymbolID.Version_2525Dch1;
        const ids = _idsBySymbolSet(lookup, version).get(symbolSet) || [];
        const seen = new Map();
        for (const id of ids) {
            const entityCode = id.slice(2, 4);
            if (entityCode === '00' || seen.has(entityCode)) continue;
            // Look up the entity root (type+subtype both "00") for the clean entity name
            const entityRootId = symbolSet + entityCode + '0000';
            const info = lookup.getMSLInfo(entityRootId, version);
            if (!info) continue;
            const name = info.getName();
            if (!name) continue;
            seen.set(entityCode, name);
        }
        result = [{ code: '00', label: 'Unspecified' }, ..._sortedOptions(seen)];
    } catch (_) {
        result = [{ code: '00', label: 'Unspecified' }];
    }
    _entityOptionsCache.set(symbolSet, result);
    return result;
}

const _entityTypeOptionsCache = new Map();

export function buildEntityTypeOptions(symbolSet, entityCode) {
    if (!entityCode || entityCode === '00') return [];
    const key = symbolSet + '|' + entityCode;
    if (_entityTypeOptionsCache.has(key)) return _entityTypeOptionsCache.get(key);
    let result;
    try {
        const lookup = MSLookup.getInstance();
        if (!lookup?.isReady()) return []; // not ready — don't cache
        const version = SymbolID.Version_2525Dch1;
        const prefix = symbolSet + entityCode;
        const ids = (_idsBySymbolSet(lookup, version).get(symbolSet) || []).filter((id) => id.startsWith(prefix));
        const seen = new Map();
        for (const id of ids) {
            const typeCode = id.slice(4, 6);
            if (typeCode === '00' || seen.has(typeCode)) continue;
            // Look up entity-type root (subtype "00") for the clean type name
            const typeRootId = prefix + typeCode + '00';
            const info = lookup.getMSLInfo(typeRootId, version);
            if (!info) continue;
            const name = info.getName();
            if (!name) continue;
            seen.set(typeCode, name);
        }
        result = seen.size ? [{ code: '00', label: 'Unspecified' }, ..._sortedOptions(seen)] : [];
    } catch (_) {
        result = [];
    }
    _entityTypeOptionsCache.set(key, result);
    return result;
}

const _entitySubTypeOptionsCache = new Map();

export function buildEntitySubTypeOptions(symbolSet, entityCode, typeCode) {
    if (!typeCode || typeCode === '00') return [];
    const key = symbolSet + '|' + entityCode + '|' + typeCode;
    if (_entitySubTypeOptionsCache.has(key)) return _entitySubTypeOptionsCache.get(key);
    let result;
    try {
        const lookup = MSLookup.getInstance();
        if (!lookup?.isReady()) return []; // not ready — don't cache
        const version = SymbolID.Version_2525Dch1;
        const prefix = symbolSet + entityCode + typeCode;
        const ids = (_idsBySymbolSet(lookup, version).get(symbolSet) || []).filter((id) => id.startsWith(prefix));
        const seen = new Map();
        for (const id of ids) {
            const subCode = id.slice(6, 8);
            if (subCode === '00' || seen.has(subCode)) continue;
            const info = lookup.getMSLInfo(id, version);
            if (!info) continue;
            const name = info.getName();
            if (!name) continue;
            seen.set(subCode, name);
        }
        result = seen.size ? [{ code: '00', label: 'Unspecified' }, ..._sortedOptions(seen)] : [];
    } catch (_) {
        result = [];
    }
    _entitySubTypeOptionsCache.set(key, result);
    return result;
}

export function buildModifierOptions(symbolSet, sector) {
    try {
        const smu = SectorModUtils.getInstance();
        if (!smu?.isReady()) return [];
        // symbolSet is a 2-digit string like "10"; SymbolID constants are numbers
        const ssInt = parseInt(symbolSet, 10);
        const mods = smu.getSectorModList(SymbolID.Version_2525Dch1, ssInt, sector);
        if (!mods?.length) return [];
        // Each entry is [code, label]; skip the "00"/"Unspecified" entry — caller adds it
        return mods
            .filter(([code]) => code !== '00')
            .map(([code, label]) => ({ code, label }));
    } catch (_) {
        return [];
    }
}
